import React, { useState, useMemo } from "react";
import { View, TouchableOpacity, ActivityIndicator } from "react-native";
import { Text, TouchableRipple, Searchbar } from "react-native-paper";
import { FieldError } from "react-hook-form";
import { gql, useQuery } from "@apollo/client";

import Modal from "./Modal";
import TextInput, { Icon as TextInputIcon } from "./TextInput";
import ModalHeader from "./Modal/ModalHeader";
import { colors } from "../../theme";
import splitMatch from "../utils/splitMatch";
import SectionsIcon from "../svgs/SectionsIcon";

// Define the GraphQL query for CRM sections
export const CRM_SECTIONS_QUERY = gql`
  query GetSections($input: MembershipCRMSectionsInput) {
    crmSections(input: $input) {
      id
      name
      legacyId
    }
  }
`;

// Define types for the query response
interface SectionType {
  id: string;
  name: string;
  legacyId: string;
}

interface SectionsQueryData {
  crmSections: SectionType[];
}

interface SharedProps {
  description: string;
  onChange(sectionId: string): void;
  placeholder?: string;
  testID?: string;
  title: string;
  value: string;
}

export interface SectionPickerInputProps extends SharedProps {
  error?: FieldError;
  onBlur?: () => void;
}

type SectionPickerProps = {
  visible: boolean;
  onDismiss(): void;
  hasBorder?: boolean;
  description: string;
  onChange(sectionId: string): void;
  placeholder?: string;
  testID?: string;
  title: string;
};

const SuggestionItem = ({
  id,
  name,
  searchText,
  onPressSuggestion,
}: SectionType & { searchText: string; onPressSuggestion(id: string): void }) => {
  const segments = useMemo(() => splitMatch(name, searchText), [name, searchText]);

  return (
    <TouchableRipple onPress={() => onPressSuggestion(id)}>
      <View className="flex-row items-center py-3 px-12">
        <View className="flex-1">
          <Text>
            {segments.map(({ text, isHighlighted }, i) => (
              <Text
                key={i}
                numberOfLines={1}
                className={`text-regular-tight ${isHighlighted ? "font-700" : "font-400"} text-ink-darkest`}
              >
                {text}
              </Text>
            ))}
          </Text>
        </View>
      </View>
    </TouchableRipple>
  );
};

const ItemSeparatorComponent = () => <View className="border-b border-sky mx-6" />;

const renderItem =
  (searchText: string, onPressSuggestion: (sectionId: string) => void) =>
  ({ item: { id, name, legacyId } }: { item: SectionType }) => (
    <SuggestionItem key={id} {...{ id, name, legacyId, searchText, onPressSuggestion }} />
  );

export function SectionPicker({
  description,
  onChange,
  placeholder,
  testID,
  title,
  hasBorder,
  onDismiss,
  visible,
}: SectionPickerProps) {
  const [searchText, setSearchText] = useState("");
  const { data, loading, error } = useQuery<SectionsQueryData>(CRM_SECTIONS_QUERY, {
    variables: { input: { search: searchText || undefined } },
  });

  // Add empty "Select a section" option to the beginning of the list
  const sectionOptions = useMemo(() => {
    if (data?.crmSections) {
      return [{ id: "", name: "Select a section", legacyId: "" }, ...data.crmSections];
    }
    return [{ id: "", name: "Select a section", legacyId: "" }];
  }, [data]);

  const filteredSections = useMemo(() => {
    if (!searchText) return sectionOptions.slice(1); // Skip the "Select a section" option when showing all
    return sectionOptions.filter(
      (section) => section.id !== "" && section.name.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [searchText, sectionOptions]);

  function onPressSuggestion(sectionId: string) {
    onChange(sectionId);
    onDismiss();
  }

  const HeaderComponent = (
    <>
      <ModalHeader {...{ title, hasBorder, onDismiss }} />
      <View className="p-6 bg-white">
        <Text className="text-small-normal mb-8 font-400 text-ink-dark">{description}</Text>
        <Searchbar
          autoFocus
          elevation={0}
          placeholderTextColor={colors.ink.light}
          style={{
            backgroundColor: "white",
            borderColor: colors.sky.dark,
            borderRadius: 8,
            borderWidth: 1,
          }}
          onChangeText={setSearchText}
          placeholder={placeholder || "Search sections"}
          value={searchText}
          testID={testID ? `${testID}-search` : undefined}
        />
        {loading && (
          <View className="justify-center items-center mt-4">
            <ActivityIndicator size="small" color={colors.primary.DEFAULT} />
          </View>
        )}
        {error && (
          <View className="justify-center items-center mt-4">
            <Text className="text-ink-light text-regular-tight">Failed to load sections.</Text>
          </View>
        )}
        {!loading && !error && filteredSections.length === 0 && (
          <View className="justify-center items-center mt-4">
            <Text className="text-ink-light text-regular-tight">No results found.</Text>
          </View>
        )}
      </View>
    </>
  );

  return (
    <Modal
      fullScreen
      webStyle={{ height: "75vh" }}
      flatListProps={{
        ItemSeparatorComponent,
        renderItem: renderItem(searchText, onPressSuggestion),
        data: filteredSections,
        extraData: searchText,
      }}
      {...{
        visible,
        HeaderComponent,
        onDismiss,
        title,
        hasBorder,
      }}
    />
  );
}

export default function SectionPickerInput({
  onChange,
  value,
  title,
  description,
  testID,
  placeholder,
  error,
  onBlur,
}: SectionPickerInputProps) {
  const [showModal, setShowModal] = useState(false);
  const { data } = useQuery<SectionsQueryData>(CRM_SECTIONS_QUERY, {
    variables: { input: {} },
  });

  // Find the selected section based on value
  const sectionOptions = useMemo(() => {
    if (data?.crmSections) {
      return [{ id: "", name: "Select a section", legacyId: "" }, ...data.crmSections];
    }
    return [{ id: "", name: "Select a section", legacyId: "" }];
  }, [data]);

  const selectedSection = useMemo(() => sectionOptions.find((section) => section.id === value), [sectionOptions, value]);

  function onPress() {
    setShowModal(true);
  }

  function hideModal() {
    setShowModal(false);
    if (onBlur) onBlur();
  }

  return (
    <View>
      <TouchableOpacity activeOpacity={0.7} onPress={onPress} testID={testID}>
        <TextInput
          editable={false}
          pointerEvents="none"
          left={<TextInputIcon icon={() => <SectionsIcon width={24} height={24} />} onPress={onPress} />}
          value={selectedSection ? selectedSection.name : ""}
          placeholder={placeholder || "Section"}
          onBlur={onBlur}
          error={error}
        />
      </TouchableOpacity>

      <SectionPicker onDismiss={hideModal} visible={showModal} onChange={onChange} {...{ title, description, placeholder }} />
    </View>
  );
}
